import { HrButton } from '@components/button/button';
import { Checkbox } from '@components/form/checkbox';
import { Input } from '@components/form/input';
import {
  GridContainer,
  HrButtons,
  HrDarkGrey,
  HrFlexContainer,
  HrIconButton,
  HrParagraph,
  HrPrimary,
  HrRed,
  HrTitleH3,
  Spinner
} from '@components/styles/hr-styles';
import { EditorConvertToHTML } from '@components/text-editor/text-editor';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ReactTooltip from 'react-tooltip';
import styled, { css } from 'styled-components';
import { useToast } from '@/hook/useToast';
import { SelectForm } from '@/components/form/select';
import { MultipleFile } from '@/components/multipleFiles/MultipleFile';
import { _modality, job, searchReason } from '@/constant/vacancy';
import { Modal } from '@/postulation_form/components/modal';
import {
  handleMutationDelete,
  handleMutationModify,
  handleMutationReject,
  handleMutationSuccess
} from '@/vacancy_request/helpers/mutationHelpers';
import {
  approveRequest,
  deleteRequest,
  getDataForRequests,
  modifyRequest,
  rejectRequest,
  saveNewRequest,
  saveNewRequestByTemplate,
  updateRequest,
  getVacancyRequestTemplateByHash
} from '@/vacancy_request/services/api';
import { getFormRequestModeConfig, FORM_REQUEST_MODE } from '@/vacancy_request/constants/form-request-mode';
import storeRequest from '@/vacancy_request/store/storeRequest';
import { languageOptions } from '@/vacancy_request/components/template-request-form/applicant-information-tab/contants';
import { CustomFieldsAnswers } from '@/vacancy_request/components/template-request-form/custom-fields/custom-fields-answers';
import {
  buildCustomFieldAnswersPayload,
  mapRequestCustomFieldAnswers,
  mapRequestCustomFields,
  mapTemplateCustomFields
} from '@/vacancy_request/components/template-request-form/custom-fields/mapper';
import { CUSTOM_FIELD_TABS } from '@/vacancy_request/components/template-request-form/custom-fields/constants';
import { useTemplateChangeGuard } from '@/vacancy_request/hooks/use-template-change-guard';
import { TemplateChangedModal } from '@/vacancy_request/components/modals/template-changed';
import { ApproveRequestModal } from '@/vacancy_request/components/modals/approve-request';
import { ApproveRequestModal as PublicApproveRequestModal } from '@/vacancy_request/components/modals/approve-public-request';
import { DeleteRequestModal } from '@/vacancy_request/components/modals/delete-request';
import { ModalSuccessRequest } from '@/vacancy_request/components/modals/modal-succes-request';
import { ModifyRequestModal } from '@/vacancy_request/components/modals/modify-modal';
import { RejectRequestModal } from '@/vacancy_request/components/modals/reject-request';
import { RequestFormStepper } from '@/vacancy_request/components/form-request-stepper';

const HrInfoTooltip = styled(ReactTooltip)`
  background-color: #ffffff !important;
  color: #3b3f51 !important;
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  max-width: 500px;
  padding: 10px 14px;
  border-radius: 8px;
  text-align: left;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  border: 1px solid #e6e6e6;

  /* Flecha */
  &.place-top:after {
    border-top-color: #ffffff !important;
  }
  &.place-bottom:after {
    border-bottom-color: #ffffff !important;
  }
  &.place-left:after {
    border-left-color: #ffffff !important;
  }
  &.place-right:after {
    border-right-color: #ffffff !important;
  }
`;

const centeredContent = css`
  width: 100%;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
`;

const MainContentContainer = styled.div`
  ${centeredContent}
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FlexContainer = styled(HrFlexContainer)`
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const HrButtonRequest = styled(HrButton)`
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const GridContainerResponsive = styled(GridContainer)`
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 16px;
  }

  @media (max-width: 425px) {
    padding: 16px;
  }
`;

const FlexContainerButton = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  @media (max-width: 425px) {
    padding: 16px;
    align-items: stretch;
  }
`;

const FooterActionGroup = styled(HrFlexContainer)`
  width: auto;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }

  @media (max-width: 425px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StepperContainer = styled(HrFlexContainer)`
  margin-top: 16px;
`;

const FormFieldColumn = styled(HrFlexContainer)`
  width: 100%;
  align-self: flex-start;
  gap: 0;
  grid-column: 1 / span 1;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const ApprovalCheckboxContainer = styled(HrFlexContainer)`
  width: 100%;
  align-self: flex-start;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  grid-column: 2 / span 1;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const CandidateFieldsGrid = styled(GridContainer)`
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const IframeViewer = styled.iframe`
  width: 100%;
  height: 60vh;
  border: none;
`;

const TitleDocViewer = styled.p`
  color: #3b3f51;
  font-weight: 600;
  display: inline;
`;

const DownloadDocButton = styled(HrButtons)`
  &,
  &:link,
  &:visited,
  &:hover,
  &:active,
  &:focus {
    text-decoration: none;
    color: #ffffff;
  }
`;

const parseBooleanLikeValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1 ? true : value === 0 ? false : null;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'si', 'sí', 'yes'].includes(normalized)) return true;
  if (['false', '0', 'no'].includes(normalized)) return false;
  return null;
};

const languageIdToCodeMap = {
  1: 'eng',
  2: 'fra',
  3: 'por',
  4: 'ale',
  5: 'jap',
  13: 'ita',
  1102: 'chi',
  1103: 'cor'
};

const normalizeSingleLanguageValue = (rawLanguageValue) => {
  if (rawLanguageValue === null || rawLanguageValue === undefined || rawLanguageValue === '') return null;

  if (Array.isArray(rawLanguageValue)) {
    if (!rawLanguageValue.length) return null;
    return normalizeSingleLanguageValue(rawLanguageValue[0]);
  }

  if (typeof rawLanguageValue === 'object') {
    return (
      normalizeSingleLanguageValue(rawLanguageValue.language) ||
      normalizeSingleLanguageValue(rawLanguageValue.value) ||
      null
    );
  }

  const asString = `${rawLanguageValue}`.trim();
  if (!asString) return null;

  if (languageOptions.some((option) => `${option.value}` === asString)) return asString;

  const asNumber = Number.parseInt(asString, 10);
  if (!Number.isNaN(asNumber) && languageIdToCodeMap[asNumber]) return languageIdToCodeMap[asNumber];

  return null;
};

const normalizeSelectPrimitiveValue = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') return value;

  const valueAsString = `${value}`.trim();
  if (!/^-?\d+$/.test(valueAsString)) return valueAsString;

  const parsedValue = Number.parseInt(valueAsString, 10);
  return Number.isNaN(parsedValue) ? valueAsString : parsedValue;
};

const normalizeMongoLikeIdValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value !== 'object') return value;

  return value.$oid || value._id || value.id || value.value || value.key || value;
};

const getFirstNonEmptyValue = (...values) => values.find((value) => value !== null && value !== undefined && `${value}`.trim() !== '');

const isMongoObjectId = (value) => typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);

const getSameOriginDownloadUrl = (uri) => {
  if (!uri) return uri;

  try {
    const parsedUri = new URL(uri, window.location.origin);
    return `${window.location.origin}${parsedUri.pathname}${parsedUri.search}`;
  } catch (e) {
    return uri;
  }
};

const buildSelectOptionsWithFallback = (options, selectedValue, ...labelCandidates) => {
  const normalizedOptions = Array.isArray(options) ? options : [];
  const normalizedSelectedValue = normalizeSelectPrimitiveValue(selectedValue);

  if (normalizedSelectedValue === null || normalizedSelectedValue === undefined || normalizedSelectedValue === '') {
    return normalizedOptions;
  }

  const hasSelectedOption = normalizedOptions.some((option) => `${option?.value}` === `${normalizedSelectedValue}`);
  if (hasSelectedOption) return normalizedOptions;

  // El valor va como último recurso, pero **sólo si es legible**: un id de mongo no es un label. Sin
  // esta guarda, cuando no llega la lista de opciones —ver `isReadOnlyView` más abajo— el select
  // terminaba mostrando el ObjectId del área como si fuera su nombre.
  //
  // Sin candidato usable no se agrega ninguna opción: el select queda con su placeholder, que es
  // mejor que mostrarle un id a una persona.
  const readableSelectedValue = isMongoObjectId(`${normalizedSelectedValue}`) ? undefined : selectedValue;
  const fallbackLabel = getFirstNonEmptyValue(...labelCandidates, readableSelectedValue);
  if (fallbackLabel === undefined) return normalizedOptions;

  return [
    {
      value: normalizedSelectedValue,
      label: `${fallbackLabel}`
    },
    ...normalizedOptions
  ];
};

const prependDisplayOptionIfMissing = (options, displayValue) => {
  if (!displayValue) return options;
  const normalizedOptions = Array.isArray(options) ? options : [];
  const alreadyExists = normalizedOptions.some(
    (option) => `${option?.value}` === `${displayValue}` || `${option?.label}` === `${displayValue}`
  );
  if (alreadyExists) return normalizedOptions;

  return [{ value: displayValue, label: `${displayValue}` }, ...normalizedOptions];
};

export const FormRequest = observer(
  ({ aliasAccount, dataForm, idRequisition, mode = FORM_REQUEST_MODE.CREATE, templateConfig = null, publicMutations = null }) => {
    const { register, control, formState, getValues, handleSubmit, setValue, unregister, reset, trigger, clearErrors, watch, setError } = useForm({});
    const { errors } = formState;

    const {
      dataViewRequest,
      setSubArea,
      setFormData,
      filterLocalities,
      filterProvinces,
      setValueModality,
      valueModality,
      resetSubarea,
      resetLocalityModal,
      actualRowId,
      setActualRowId,
      setAreaSubareas,
      resetAreaSubareas,
      setClientSubclients,
      resetClientSubclients
    } = storeRequest();

    const [t] = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [modalSucces, setModalSuccess] = useState(false);
    const [isFormLoaded, setIsFormLoaded] = useState(false);
    const [formLoadError, setFormLoadError] = useState(false);
    const [accountyType, setAccountType] = useState('');
    const [showModalApproveRequest, setShowModalApproveRequest] = useState(false);
    const [showModalModifyRequest, setShowModalModifyRequest] = useState(false);
    const [showModalRejectRequest, setShowModalRejectRequest] = useState(false);
    const [showModalDeleteRequest, setShowModalDeleteRequest] = useState(false);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [showViewerDocs, setShowViewerDocs] = useState(false);
    const [editors, setEditors] = useState({ descript: '', requirements: '', benefits: '' });
    const [isCreated, setIsCreated] = useState(false);
    const [showDocuments, setShowDocuments] = useState({});
    const refCurrent = useRef(null);
    const [prefilledCompany, setPrefilledCompany] = useState(false);
    const [prefilledConsultant, setPrefilledConsultant] = useState(false);
    const [templateDefaultsApplied, setTemplateDefaultsApplied] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const selectedAreaValue = watch('area');
    const selectedClientValue = watch('client');
    const selectedSubareaValue = watch('subarea');
    const selectedSubclientValue = watch('subclient');

    const {
      viewType,
      isPublicView,
      isTemplateFlow,
      isStepperMode,
      showReadOnlyBackButton,
      canShowPublicDecisionActions
    } = getFormRequestModeConfig({ mode, dataForm });
    const steps = [
      { id: 'general', label: '1. Información general' },
      { id: 'vacancy', label: '2. Información de la vacante' },
      { id: 'description', label: '3. Información del candidato' }
    ];

    const FIELD_TO_STEP = {
      applicantName: 0, applicantEmail: 0, portalData: 0,
      area: 0, areaApprovalRequired: 0, subarea: 0, subareaApprovalRequired: 0,
      client: 0, subclient: 0, localityData: 0,
      name: 1, c_area: 1, c_subarea: 1, c_modality: 1,
      search_reason: 1, count_post: 1, type: 1, c_type_jobs: 1, apto_discapacitados: 1
    };

    /*
     * De dónde sale la definición de los campos fijos, y es la contraparte de D4 para ellos:
     * en lectura, del snapshot guardado en la solicitud; al cargar y al editar, de la plantilla
     * vigente, porque se está produciendo un envío nuevo.
     *
     * Alcanza con cambiar el origen acá: `getTemplateField`, `isFieldVisible` e `isFieldRequired`
     * leen todos de esta variable, así que toda la vista de lectura pasa a dibujarse con el label y
     * la obligatoriedad que tenía la plantilla al responder.
     *
     * Las solicitudes anteriores a este feature no tienen snapshot: caen en la plantilla vigente,
     * que es exactamente como se comportaban hasta ahora.
     */
    const requestFieldsSnapshot = Array.isArray(dataForm?.fields) ? dataForm.fields : [];

    /*
     * Dos `key` de plantilla no coinciden con el nombre que el campo tiene en el formulario. La
     * unidad de negocio no se vuelca desde el snapshot: se responde con tres claves y el control las
     * lee planas, así que se deja como está.
     */
    const SNAPSHOT_FORM_FIELD_NAME = { type: 'c_type_jobs', additionalComments: 'descript' };
    const getSnapshotFormFieldName = (fieldKey) => SNAPSHOT_FORM_FIELD_NAME[fieldKey] || fieldKey;
    const currentTemplateFields = Array.isArray(templateConfig?.fields) ? templateConfig.fields : [];
    const shouldUseFieldsSnapshot = viewType === 'readOnly' && requestFieldsSnapshot.length > 0;
    const templateFields = shouldUseFieldsSnapshot ? requestFieldsSnapshot : currentTemplateFields;
    const useTemplateConfig = templateFields.length > 0;
    // De dónde sale la lista de campos custom depende del modo, y es la decisión D4:
    // - lectura: del snapshot guardado en la solicitud, para que editar la plantilla no cambie
    //   retroactivamente lo que muestra una solicitud ya enviada;
    // - carga y edición: de la plantilla vigente, porque se está produciendo un envío nuevo.
    const templateCustomFields =
      viewType === 'readOnly' ? mapRequestCustomFields(dataForm) : mapTemplateCustomFields(templateConfig);

    // Detección de cambios en la plantilla + borrador temporal (E6). En lectura no aplica: no hay
    // nada que enviar y la vista se dibuja desde el snapshot.
    const templateChangeGuard = useTemplateChangeGuard({
      draftKey: idRequisition || dataForm?._id || templateConfig?.hash || null,
      currentTemplateVersion: templateConfig?.version,
      // En edición la plantilla no se re-consulta sola, así que el chequeo del submit la pide.
      fetchCurrentTemplateVersion: async () => {
        if (!aliasAccount || !templateConfig?.hash) return null;

        try {
          const latestTemplate = await getVacancyRequestTemplateByHash(aliasAccount, templateConfig.hash);
          return Number(latestTemplate?.version);
        } catch (error) {
          return null;
        }
      },
      isEnabled: useTemplateConfig && viewType !== 'readOnly'
    });
    const REQUIRED_FIELD_CONFIG = {
      DISABLED: 0,
      REQUIRED: 1,
      OPTIONAL: 2
    };

    const normalizeTemplateFieldLookupKey = (key) => {
      if (accountyType !== 'consultant') return key;
      const templateUsesClientKey = templateFields.some((f) => f?.key === 'client');
      const templateUsesSubclientKey = templateFields.some((f) => f?.key === 'subclient');
      if (key === 'client' || key === 'area') return templateUsesClientKey ? 'client' : 'area';
      if (key === 'subclient' || key === 'subarea') return templateUsesSubclientKey ? 'subclient' : 'subarea';
      return key;
    };

    const getTemplateField = (key) => {
      const normalizedKey = normalizeTemplateFieldLookupKey(key);
      return templateFields.find((field) => typeof field?.key === 'string' && field.key === normalizedKey);
    };

    const getTemplateFieldDefaultName = (key) => {
      const field = getTemplateField(key);
      return field?.default_name || field?.defaultName || '';
    };

    const getTemplateFieldDefaultValue = (key) => {
      const field = getTemplateField(key);
      return field?.default ?? null;
    };

    const normalizeTemplateRequiredLevel = (value, defaultLevel = REQUIRED_FIELD_CONFIG.OPTIONAL) => {
      if (value === null || value === undefined) return defaultLevel;

      if (typeof value === 'number' && [0, 1, 2].includes(value)) return value;

      if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();
        if (normalizedValue === '0') return REQUIRED_FIELD_CONFIG.DISABLED;
        if (normalizedValue === '1') return REQUIRED_FIELD_CONFIG.REQUIRED;
        if (normalizedValue === '2') return REQUIRED_FIELD_CONFIG.OPTIONAL;
        if (normalizedValue === 'true') return REQUIRED_FIELD_CONFIG.REQUIRED;
        if (normalizedValue === 'false') return REQUIRED_FIELD_CONFIG.OPTIONAL;
      }

      if (value === true) return REQUIRED_FIELD_CONFIG.REQUIRED;
      if (value === false) return REQUIRED_FIELD_CONFIG.OPTIONAL;

      return defaultLevel;
    };

    const getTemplateFieldRequiredLevel = (key, defaultLevel = REQUIRED_FIELD_CONFIG.OPTIONAL) => {
      if (!useTemplateConfig) return defaultLevel;

      const field = getTemplateField(key);
      if (!field) return REQUIRED_FIELD_CONFIG.DISABLED;
      if (Array.isArray(field.required)) return defaultLevel;

      return normalizeTemplateRequiredLevel(field.required, defaultLevel);
    };

    const getLocalityFieldRequiredLevel = (localityKey, defaultLevel = REQUIRED_FIELD_CONFIG.OPTIONAL) => {
      if (!useTemplateConfig) return defaultLevel;

      const localityField = getTemplateField('localityData');
      if (!localityField) return REQUIRED_FIELD_CONFIG.DISABLED;

      if (Array.isArray(localityField.required)) {
        const localityConfig = localityField.required.find((field) => field?.key === localityKey);
        if (!localityConfig) return REQUIRED_FIELD_CONFIG.DISABLED;
        return normalizeTemplateRequiredLevel(localityConfig.required, defaultLevel);
      }

      return normalizeTemplateRequiredLevel(localityField.required, defaultLevel);
    };

    const isFieldVisible = (keys, defaultValue = true) => {
      if (!useTemplateConfig) return defaultValue;
      const keysToCheck = Array.isArray(keys) ? keys : [keys];
      return keysToCheck.some(
        (key) => getTemplateFieldRequiredLevel(key, REQUIRED_FIELD_CONFIG.OPTIONAL) !== REQUIRED_FIELD_CONFIG.DISABLED
      );
    };

    const isFieldRequired = (key, defaultValue = false) => {
      if (!useTemplateConfig || (viewType !== 'create' && viewType !== 'readOnly' && viewType !== 'edit')) return defaultValue;
      return (
        getTemplateFieldRequiredLevel(
          key,
          defaultValue ? REQUIRED_FIELD_CONFIG.REQUIRED : REQUIRED_FIELD_CONFIG.OPTIONAL
        ) === REQUIRED_FIELD_CONFIG.REQUIRED
      );
    };

    const isLocalityFieldVisible = (key, defaultValue = true) => {
      if (!useTemplateConfig) return defaultValue;
      return getLocalityFieldRequiredLevel(key, REQUIRED_FIELD_CONFIG.OPTIONAL) !== REQUIRED_FIELD_CONFIG.DISABLED;
    };

    const isLocalityFieldRequired = (key, defaultValue = true) => {
      if (!useTemplateConfig) return defaultValue;
      return (
        getLocalityFieldRequiredLevel(
          key,
          defaultValue ? REQUIRED_FIELD_CONFIG.REQUIRED : REQUIRED_FIELD_CONFIG.OPTIONAL
        ) === REQUIRED_FIELD_CONFIG.REQUIRED
      );
    };

    const areaRequiredLevel = getTemplateFieldRequiredLevel('area', REQUIRED_FIELD_CONFIG.OPTIONAL);
    const subareaRequiredLevel = getTemplateFieldRequiredLevel('subarea', REQUIRED_FIELD_CONFIG.OPTIONAL);
    const areaApprovalRequiredLevel = getTemplateFieldRequiredLevel(
      'areaApprovalRequired',
      REQUIRED_FIELD_CONFIG.OPTIONAL
    );
    const subareaApprovalRequiredLevel = getTemplateFieldRequiredLevel(
      'subareaApprovalRequired',
      REQUIRED_FIELD_CONFIG.OPTIONAL
    );

    const showAreaField = !useTemplateConfig || areaRequiredLevel !== REQUIRED_FIELD_CONFIG.DISABLED;
    const showSubareaField =
      showAreaField && (!useTemplateConfig || subareaRequiredLevel !== REQUIRED_FIELD_CONFIG.DISABLED);
    const templatePortalDefault = getTemplateFieldDefaultValue('business_group_type');
    const templatePortalDefaultName = getTemplateFieldDefaultName('business_group_type');
    const portalDataOptions = useMemo(
      () => (Array.isArray(dataViewRequest.portalData) ? dataViewRequest.portalData : []),
      [dataViewRequest.portalData]
    );
    const shouldUsePortalDisplayFallback = portalDataOptions.length === 0 && Boolean(templatePortalDefaultName);
    const portalSelectPlaceholder = shouldUsePortalDisplayFallback ? templatePortalDefaultName : undefined;
    const portalDataOptionsSignature = useMemo(
      () =>
        portalDataOptions
          .map((option) =>
            [
              `${option?.type || ''}`.trim().toLowerCase(),
              `${option?.value || ''}`.trim().toLowerCase(),
              `${option?.business_group_id || ''}`.trim().toLowerCase(),
              `${option?.business_group_key || option?.key || ''}`.trim().toLowerCase()
            ].join('|')
          )
          .join('||'),
      [portalDataOptions]
    );
    const hasBusinessGroupSelectionInRequest = useMemo(() => {
      const type = `${dataForm?.business_group_type || ''}`.trim().toLowerCase();
      const id = `${dataForm?.business_group_id || ''}`.trim();
      const key = `${dataForm?.business_group_key || ''}`.trim();

      if (!type) return false;
      if (type === 'microsite') return Boolean(id || key);
      if (type === 'portal') return Boolean(key);
      if (type === 'referrers_program' || type === 'referents_program') return true;
      return Boolean(id || key);
    }, [dataForm]);
    const shouldShowBusinessGroupField =
      viewType === 'create' ? isFieldVisible('business_group_type', false) : hasBusinessGroupSelectionInRequest;
    const isAreaFieldRequired = useTemplateConfig ? areaRequiredLevel === REQUIRED_FIELD_CONFIG.REQUIRED : false;
    const isSubareaFieldRequired = useTemplateConfig ? subareaRequiredLevel === REQUIRED_FIELD_CONFIG.REQUIRED : false;
    const showAreaApprovalCheckbox = showAreaField;
    const showSubareaApprovalCheckbox =
      showSubareaField && (!useTemplateConfig || Boolean(getTemplateField('subareaApprovalRequired')));
    const forceAreaApprovalRequired = useTemplateConfig
      ? areaApprovalRequiredLevel === REQUIRED_FIELD_CONFIG.REQUIRED
      : false;
    const forceSubareaApprovalRequired = useTemplateConfig
      ? subareaApprovalRequiredLevel === REQUIRED_FIELD_CONFIG.REQUIRED
      : false;

    const countryRequiredLevel = getLocalityFieldRequiredLevel('country', REQUIRED_FIELD_CONFIG.OPTIONAL);
    const hasCountryConfig = Array.isArray(getTemplateField('localityData')?.required)
      ? getTemplateField('localityData')?.required?.some((field) => field?.key === 'country')
      : false;
    const showCountryField = isLocalityFieldVisible('country', true);
    const shouldShowDependentLocalityFieldsByCountry = hasCountryConfig
      ? countryRequiredLevel === REQUIRED_FIELD_CONFIG.REQUIRED ||
        countryRequiredLevel === REQUIRED_FIELD_CONFIG.OPTIONAL
      : null;
    const showProvinceField =
      shouldShowDependentLocalityFieldsByCountry === null
        ? isLocalityFieldVisible('zona', true)
        : shouldShowDependentLocalityFieldsByCountry;
    const showLocalityField =
      shouldShowDependentLocalityFieldsByCountry === null
        ? isLocalityFieldVisible('locality', true)
        : shouldShowDependentLocalityFieldsByCountry;
    const showLocalityFields = showCountryField || showProvinceField || showLocalityField;
    const templateDefaultAreaValue = getTemplateFieldDefaultValue('area');
    const templateDefaultClientValue = getTemplateFieldDefaultValue('client');
    const templateDefaultSubareaValue = getTemplateFieldDefaultValue('subarea');
    const templateDefaultSubclientValue = getTemplateFieldDefaultValue('subclient');
    const templateDefaultAreaName = getTemplateFieldDefaultName('area');
    const templateDefaultSubareaName =
      getTemplateFieldDefaultName('subarea') ||
      dataForm?.subarea_name ||
      dataForm?.subareaName ||
      dataForm?.subarea_label ||
      dataForm?.subareaLabel ||
      '';
    const resolvedAreaValue = selectedAreaValue ?? dataForm?.area ?? templateDefaultAreaValue;
    const resolvedClientValue = selectedClientValue ?? dataForm?.client ?? templateDefaultClientValue;
    const resolvedSubareaValue = selectedSubareaValue ?? dataForm?.subarea ?? templateDefaultSubareaValue;
    const resolvedSubclientValue = selectedSubclientValue ?? dataForm?.subclient ?? templateDefaultSubclientValue;
    const shouldUseAreaDisplayFallback = (dataViewRequest.areas?.length ?? 0) === 0 && Boolean(templateDefaultAreaName);
    const shouldUseSubareaDisplayFallback =
      (dataViewRequest.subareas?.length ?? 0) === 0 && Boolean(templateDefaultSubareaName);
    const areaSelectPlaceholder = shouldUseAreaDisplayFallback ? templateDefaultAreaName : undefined;
    const subareaSelectPlaceholder = shouldUseSubareaDisplayFallback ? templateDefaultSubareaName : undefined;
    const hasSelectedArea = resolvedAreaValue !== null && resolvedAreaValue !== undefined && `${resolvedAreaValue}` !== '';
    const hasSelectedClient = resolvedClientValue !== null && resolvedClientValue !== undefined && `${resolvedClientValue}` !== '';
    const hasSelectedSubarea = resolvedSubareaValue !== null && resolvedSubareaValue !== undefined && `${resolvedSubareaValue}` !== '';
    const hasSelectedSubclient = resolvedSubclientValue !== null && resolvedSubclientValue !== undefined && `${resolvedSubclientValue}` !== '';
    const hasAvailableSubareas = (dataViewRequest.subareas?.length ?? 0) > 0;
    const hasResolvableSubareaSelection = hasAvailableSubareas || hasSelectedSubarea;
    const shouldRequireSubarea = isSubareaFieldRequired && hasAvailableSubareas;
    const effectiveForceSubareaApprovalRequired = forceSubareaApprovalRequired && hasResolvableSubareaSelection;
    const companyAreaOptions = useMemo(
      () => {
        const options = buildSelectOptionsWithFallback(
          dataViewRequest.areas,
          resolvedAreaValue,
          templateDefaultAreaName,
          dataForm?.area_name,
          dataForm?.areaName,
          dataForm?.area_label,
          dataForm?.areaLabel
        );

        return prependDisplayOptionIfMissing(options, templateDefaultAreaName);
      },
      [
        dataForm?.area,
        dataForm?.areaLabel,
        dataForm?.areaName,
        dataForm?.area_label,
        dataForm?.area_name,
        dataViewRequest.areas,
        resolvedAreaValue,
        shouldUseAreaDisplayFallback,
        templateFields,
        templateDefaultAreaName,
        templateDefaultAreaValue
      ]
    );
    const companySubareaOptions = useMemo(
      () => {
        const options = buildSelectOptionsWithFallback(
          dataViewRequest.subareas,
          resolvedSubareaValue,
          templateDefaultSubareaName,
          dataForm?.subarea_name,
          dataForm?.subareaName,
          dataForm?.subarea_label,
          dataForm?.subareaLabel
        );

        return prependDisplayOptionIfMissing(options, templateDefaultSubareaName);
      },
      [
        dataForm?.subarea,
        dataForm?.subareaLabel,
        dataForm?.subareaName,
        dataForm?.subarea_label,
        dataForm?.subarea_name,
        dataViewRequest.subareas,
        resolvedSubareaValue,
        shouldUseSubareaDisplayFallback,
        templateFields,
        templateDefaultSubareaName,
        templateDefaultSubareaValue
      ]
    );
    const consultantClientOptions = useMemo(
      () =>
        buildSelectOptionsWithFallback(
          dataViewRequest.clients,
          resolvedClientValue,
          dataForm?.client_name,
          dataForm?.clientName,
          dataForm?.client_label,
          dataForm?.clientLabel
        ),
      [
        dataForm?.client,
        dataForm?.clientLabel,
        dataForm?.clientName,
        dataForm?.client_label,
        dataForm?.client_name,
        dataViewRequest.clients,
        resolvedClientValue,
        selectedClientValue
      ]
    );
    const consultantSubclientOptions = useMemo(
      () =>
        buildSelectOptionsWithFallback(
          dataViewRequest.subclients,
          resolvedSubclientValue,
          dataForm?.subclient_name,
          dataForm?.subclientName,
          dataForm?.subclient_label,
          dataForm?.subclientLabel
        ),
      [
        dataForm?.subclient,
        dataForm?.subclientLabel,
        dataForm?.subclientName,
        dataForm?.subclient_label,
        dataForm?.subclient_name,
        dataViewRequest.subclients,
        resolvedSubclientValue,
        selectedSubclientValue
      ]
    );

    const toggleViewerDocs = (docfile) => {
      setShowDocuments(docfile);
      setShowViewerDocs(!showViewerDocs);
    };

    const { executeRecaptcha } = useGoogleReCaptcha();

    useEffect(() => {
      setValue('count_post', 1);

      if (!isFormLoaded) {
        const loadInitialFormData = async () => {
          try {
            // Los dos modos de sólo lectura, no sólo el interno: en una solicitud ya enviada lo que
            // hace falta es **resolver el nombre de lo que se eligió**, no ofrecer opciones. Filtrar
            // por plantilla ahí devuelve las listas vacías cuando la plantilla tiene el campo
            // deshabilitado, y el select se queda sin de dónde sacar la etiqueta.
            //
            // El modo público quedaba afuera, así que en el link que llega por email el área y la
            // subárea se veían con su ObjectId como nombre.
            const isReadOnlyView =
              mode === FORM_REQUEST_MODE.INTERNAL_READ_ONLY || mode === FORM_REQUEST_MODE.PUBLIC_READ_ONLY;
            const templateHash = isReadOnlyView ? null : templateConfig?.hash || null;
            const orgId = isReadOnlyView ? dataForm?.client || dataForm?.area || null : null;
            const response = await getDataForRequests(aliasAccount, templateHash, orgId);
            if (!response) {
              throw new Error('Empty response while loading form data');
            }

            setAccountType(response.accountType);

            const dataToLoad = {
              nameAccount: response.nameAccount,
              imagePhoto: response.imagePhoto,
              accountType: response.accountType,
              areas: {
                items: Array.isArray(response.areas?.response?.areas)
                  ? response.areas.response.areas
                  : [response.areas?.response?.areas].filter(Boolean),
                labelField: 'nombre',
                valueField: 'id'
              },
              subareas: response.subareas,
              clients: {
                items: Array.isArray(response.clients?.response?.clientes)
                  ? response.clients.response.clientes
                  : [response.clients?.response?.clientes].filter(Boolean),
                labelField: 'compañia',
                valueField: 'id'
              },
              subclients: response.subclients,
              locality: { items: response.locality, labelField: 'name', valueField: 'id' },
              countries: { items: response.countries.response.pais, labelField: 'nombre', valueField: 'id' },
              employmentModalities: {
                items: response.employmentModalities.response.modalidadEmpleo,
                labelField: 'nombre',
                valueField: 'id'
              },
              c_areas: { items: response.c_areas.response.area, labelField: 'nombre', valueField: 'id' },
              c_subAreas: {
                items: response.c_subAreas.response,
                labelField: 'nombre',
                valueField: 'id',
                subItemsField: 'area'
              },
              portalData: Array.isArray(response.portalData) ? response.portalData : [],
              localities: {
                items: response.localities.response,
                labelField: 'nombre',
                valueField: 'id',
                subItemsField: 'provinceId'
              },
              provinces: {
                items: response.provinces.response,
                labelField: 'nombre',
                valueField: 'id',
                subItemsField: 'countryId'
              },
              employmentTypes: response.employmentTypes?.response?.tipoEmpleo,
              hierarchies: { items: response.hierarchies.response.jerarquia, labelField: 'nombre', valueField: 'id' }
            };

            setFormData(dataToLoad);
            setFormLoadError(false);
          } catch (error) {
            console.error('Error loading vacancy request form data:', error);
            setFormLoadError(true);
          } finally {
            setIsFormLoaded(true);
          }
        };

        loadInitialFormData();
      }
    }, [aliasAccount, isFormLoaded, mode, setFormData, setValue, templateConfig?.hash]);

    useEffect(() => {
      if (!isFormLoaded || !['readOnly', 'edit'].includes(viewType)) return;

      const dataFormFilter = { ...(dataForm ?? {}) };
      const cModality = dataFormFilter.c_modality ?? dataFormFilter.modalidadTrabajo;
      const normalizedExperienceValue = parseBooleanLikeValue(
        dataFormFilter.experience ?? dataFormFilter.hasExperience ?? dataFormFilter.conExperiencia
      );
      const normalizedPeopleInChargeValue = parseBooleanLikeValue(
        dataFormFilter.hasPeopleInCharge ??
          dataFormFilter.peopleInCharge ??
          dataFormFilter.genteACargo ??
          dataFormFilter.gente_a_cargo
      );
      const normalizedTravelAvailabilityValue = parseBooleanLikeValue(
        dataFormFilter.travel_availability ??
          dataFormFilter.travelAvailability ??
          dataFormFilter.disponibilidadViajar
      );
      const normalizedLanguageValue =
        normalizeSingleLanguageValue(dataFormFilter.languages) ||
        normalizeSingleLanguageValue(dataFormFilter.selectedLanguages) ||
        normalizeSingleLanguageValue(dataFormFilter.req_idioma) ||
        normalizeSingleLanguageValue(dataFormFilter.language);
      delete dataFormFilter.c_modality;
      delete dataFormFilter.modalidadTrabajo;
      if (normalizedExperienceValue !== null) dataFormFilter.experience = normalizedExperienceValue;
      if (normalizedPeopleInChargeValue !== null) dataFormFilter.hasPeopleInCharge = normalizedPeopleInChargeValue;
      if (normalizedTravelAvailabilityValue !== null) {
        dataFormFilter.travel_availability = normalizedTravelAvailabilityValue;
      }
      if (normalizedLanguageValue !== null) {
        dataFormFilter.selectedLanguages = normalizedLanguageValue;
        dataFormFilter.languages = normalizedLanguageValue;
      }

      const normalizedLocalityData = (() => {
        const localityData = dataFormFilter?.localityData;
        if (localityData && typeof localityData === 'object' && !Array.isArray(localityData)) {
          return {
            country: localityData.country ?? localityData.pais ?? null,
            zona: localityData.zona ?? localityData.provincia ?? null,
            locality: localityData.locality ?? localityData.localidad ?? null
          };
        }

        if (Array.isArray(dataFormFilter?.ubicationArray) && dataFormFilter.ubicationArray[0]) {
          const firstUbication = dataFormFilter.ubicationArray[0];
          return {
            country: firstUbication.country ?? firstUbication.pais ?? null,
            zona: firstUbication.zona ?? firstUbication.provincia ?? null,
            locality: firstUbication.locality ?? firstUbication.localidad ?? null
          };
        }

        return null;
      })();

      if (normalizedLocalityData) {
        dataFormFilter.localityData = normalizedLocalityData;
      }

      const resolvePortalDataValue = () => {
        if (!Array.isArray(portalDataOptions) || portalDataOptions.length === 0) return '';

        const requestType = `${dataFormFilter.business_group_type || ''}`.trim().toLowerCase();
        const requestIdRaw = dataFormFilter.business_group_id;
        const requestId =
          requestIdRaw && typeof requestIdRaw === 'object'
            ? `${requestIdRaw.$oid || requestIdRaw._id || requestIdRaw.id || ''}`.trim()
            : `${requestIdRaw || ''}`.trim();
        const requestKey = `${dataFormFilter.business_group_key || ''}`.trim().toLowerCase();
        if (!requestType) return '';

        const matchedOption = portalDataOptions.find((option) => {
          const optionType = `${option?.type || ''}`.trim().toLowerCase();
          const optionValue = `${option?.value || ''}`.trim();
          const optionIdRaw = option?.business_group_id;
          const optionId =
            optionIdRaw && typeof optionIdRaw === 'object'
              ? `${optionIdRaw.$oid || optionIdRaw._id || optionIdRaw.id || ''}`.trim()
              : `${optionIdRaw || ''}`.trim();
          const optionKey = `${option?.business_group_key || option?.key || ''}`.trim().toLowerCase();
          const optionValueLower = optionValue.toLowerCase();

          if (requestType === 'microsite') {
            if (optionType !== 'microsite') return false;
            return (
              (requestId && (optionId === requestId || optionValue === requestId || optionValueLower === requestId.toLowerCase())) ||
              (requestKey && (optionKey === requestKey || optionValueLower === requestKey))
            );
          }

          if (requestType === 'portal') {
            if (optionType !== 'portal') return false;
            return (
              (requestKey && (optionKey === requestKey || optionValueLower === requestKey)) ||
              (!requestKey && optionKey === optionValueLower)
            );
          }

          if (requestType === 'referrers_program' || requestType === 'referents_program') {
            return (
              optionType === 'referrers_program' ||
              optionValueLower === 'referrers_program' ||
              optionValueLower === 'referents_program' ||
              optionKey === 'referrers_program' ||
              optionKey === 'referents_program'
            );
          }

          return false;
        });

        if (matchedOption?.value) return matchedOption.value;
        if (requestType === 'microsite') return requestId || requestKey || '';
        if (requestType === 'portal') return requestKey || '';
        if (requestType === 'referrers_program' || requestType === 'referents_program') return 'referrers_program';
        return '';
      };

      const selectedPortalDataValue = resolvePortalDataValue();
      if (selectedPortalDataValue) {
        dataFormFilter.portalData = selectedPortalDataValue;
      }

      const parsedModalityValue = cModality != null && !Number.isNaN(parseInt(cModality, 10))
        ? parseInt(cModality, 10)
        : null;

      if (viewType === 'edit' && parsedModalityValue !== 1 && parsedModalityValue !== 3) {
        unregister('localityData.zona');
      }

      const localityDataToApply = normalizedLocalityData || dataFormFilter?.localityData || null;
      if (localityDataToApply?.country != null && localityDataToApply?.country !== '') {
        filterProvinces(normalizeSelectPrimitiveValue(localityDataToApply.country));
      }
      if (localityDataToApply?.zona != null && localityDataToApply?.zona !== '') {
        filterLocalities(normalizeSelectPrimitiveValue(localityDataToApply.zona));
      }

      if (dataFormFilter) {
        Object.keys(dataFormFilter).forEach((x) => {
          if (x === 'attachedFiles') return;
          setValue(x, dataFormFilter[x]);
        });
      }

      // Después del volcado genérico: las respuestas de los campos custom se guardan con una forma
      // distinta a la que usa el formulario, así que se reescriben con la del mapper.
      setValue('customFieldsAnswers', mapRequestCustomFieldAnswers(dataForm));

      /*
       * Y en lectura, los campos fijos se llenan desde su snapshot y no desde las claves planas
       * (D5, D44). Con esto los dos tipos de campo se comportan igual: lo que muestra una solicitud
       * enviada es lo que se respondió, y no cambia si después se edita la plantilla.
       *
       * Se pisa el volcado genérico a propósito. Las solicitudes anteriores al feature no tienen
       * snapshot, así que el array viene vacío y siguen mostrando lo plano, como hasta ahora.
       */
      if (viewType === 'readOnly') {
        requestFieldsSnapshot.forEach((snapshotField) => {
          if (snapshotField?.key === undefined || snapshotField.value === undefined) return;
          if (snapshotField.key === 'business_group_type') return;

          setValue(getSnapshotFormFieldName(snapshotField.key), snapshotField.value);
        });
      }

      if (localityDataToApply?.country != null && localityDataToApply?.country !== '') {
        setValue('localityData.country', normalizeSelectPrimitiveValue(localityDataToApply.country));
      }
      if (localityDataToApply?.zona != null && localityDataToApply?.zona !== '') {
        setValue('localityData.zona', normalizeSelectPrimitiveValue(localityDataToApply.zona));
      }
      if (localityDataToApply?.locality != null && localityDataToApply?.locality !== '') {
        setValue('localityData.locality', normalizeSelectPrimitiveValue(localityDataToApply.locality));
      }

      setEditors({
        descript: dataForm?.descript || '',
        requirements: dataForm?.requirements || '',
        benefits: dataForm?.benefits || ''
      });

      const areaValue = parseInt(getValues('c_area'));
      if (!Number.isNaN(areaValue)) setSubArea(areaValue);

      if (cModality != null && !Number.isNaN(parseInt(cModality, 10))) {
        const parsedModality = parseInt(cModality, 10);
        setValue('c_modality', parsedModality);
        if (valueModality !== parsedModality) {
          setValueModality(parsedModality);
        }
      }

      if (accountyType === 'company' && dataForm?.area) {
        setValue('area', dataForm.area);
        setAreaSubareas(dataForm.area);
        setPrefilledCompany(false);
      }
      if (accountyType === 'consultant') {
        const clientValue = dataForm?.client || dataForm?.area;
        if (clientValue) {
          setValue('client', clientValue);
          setClientSubclients(clientValue);
          setPrefilledConsultant(false);
        }
      }
    }, [
      isFormLoaded,
      viewType,
      dataForm,
      setValue,
      unregister,
      filterProvinces,
      filterLocalities,
      setSubArea,
      getValues,
      setValueModality,
      accountyType,
      setAreaSubareas,
      setClientSubclients,
      portalDataOptionsSignature
    ]);

    /*
     * Vuelca el borrador que se guardó antes de refrescar por un cambio de plantilla (E6).
     *
     * Va en su propio efecto y declarado DESPUÉS del que carga la solicitud, por dos razones:
     * - el efecto de carga sólo corre en lectura y edición, y el borrador también hace falta en el
     *   flujo de creación (D12);
     * - ese efecto sigue haciendo `setValue` de localidad, modalidad y área después del punto donde
     *   estaba el volcado, así que pisaba parte de lo recuperado. Los efectos corren en orden de
     *   declaración, así que ahora el borrador queda último y gana.
     */
    useEffect(() => {
      if (!isFormLoaded || viewType === 'readOnly') return;

      const savedDraft = templateChangeGuard.readDraft();
      if (!savedDraft) return;

      Object.keys(savedDraft).forEach((draftField) => setValue(draftField, savedDraft[draftField]));
    }, [isFormLoaded, viewType]);

    useEffect(() => {
      if (prefilledCompany) return;
      if (accountyType !== 'company') return;
      if (!dataForm?.subarea) return;

      if (viewType === 'readOnly') {
        setValue('subarea', dataForm.subarea);
        setPrefilledCompany(true);
        return;
      }

      const hasOption =
        Array.isArray(dataViewRequest.subareas) &&
        dataViewRequest.subareas.some((opt) => `${opt.value}` === `${dataForm.subarea}`);

      if (hasOption) {
        setValue('subarea', dataForm.subarea);
        setPrefilledCompany(true);
      }
    }, [accountyType, dataForm?.subarea, dataViewRequest.subareas, prefilledCompany, setValue, viewType]);

    useEffect(() => {
      if (prefilledConsultant) return;
      if (accountyType !== 'consultant') return;
      if (!dataForm?.subclient) return;

      const subclientValue = `${dataForm.subclient}`;

      if (viewType === 'readOnly') {
        setValue('subclient', subclientValue);
        setPrefilledConsultant(true);
        return;
      }

      const hasOption =
        Array.isArray(dataViewRequest.subclients) &&
        dataViewRequest.subclients.some((opt) => `${opt.value}` === subclientValue);

      if (hasOption) {
        setValue('subclient', subclientValue);
        setPrefilledConsultant(true);
      }
    }, [accountyType, dataForm?.subclient, dataViewRequest.subclients, prefilledConsultant, setValue, viewType]);

    useEffect(() => {
      if (dataViewRequest.localities.length === 1) {
        setValue('localityData.locality', dataViewRequest.localities[0]?.value);
      }
    }, [dataViewRequest.localities, setValue]);

    useEffect(() => {
      setTemplateDefaultsApplied(false);
    }, [templateConfig]);

    useEffect(() => {
      if (!isStepperMode) {
        setActiveStep(0);
      }
    }, [isStepperMode]);

    useEffect(() => {
      if (!useTemplateConfig || viewType !== 'create') return;

      setValue('areaApprovalRequired', forceAreaApprovalRequired);
      setValue('subareaApprovalRequired', effectiveForceSubareaApprovalRequired);
    }, [forceAreaApprovalRequired, effectiveForceSubareaApprovalRequired, setValue, useTemplateConfig, viewType]);

    useEffect(() => {
      if (hasResolvableSubareaSelection) return;
      setValue('subareaApprovalRequired', false);
    }, [hasResolvableSubareaSelection, setValue]);

    // Initialize approval checkboxes based on template required level for ALL modes (including readOnly)
    useEffect(() => {
      if (!useTemplateConfig || !isFormLoaded) return;

      // Process areaApprovalRequired
      const areaApprovalField = templateFields.find((field) => field?.key === 'areaApprovalRequired');
      if (areaApprovalField) {
        const areaApprovalLevel = normalizeTemplateRequiredLevel(areaApprovalField.required, REQUIRED_FIELD_CONFIG.OPTIONAL);
        setValue('areaApprovalRequired', areaApprovalLevel === REQUIRED_FIELD_CONFIG.REQUIRED);
      }

      // Process subareaApprovalRequired
      const subareaApprovalField = templateFields.find((field) => field?.key === 'subareaApprovalRequired');
      if (subareaApprovalField) {
        const subareaApprovalLevel = normalizeTemplateRequiredLevel(subareaApprovalField.required, REQUIRED_FIELD_CONFIG.OPTIONAL);
        setValue('subareaApprovalRequired', subareaApprovalLevel === REQUIRED_FIELD_CONFIG.REQUIRED);
      }
    }, [useTemplateConfig, isFormLoaded, templateFields, setValue]);

    useEffect(() => {
      if (shouldRequireSubarea) return;
      clearErrors('subarea');
    }, [clearErrors, shouldRequireSubarea]);

    useEffect(() => {
      if (!templatePortalDefault) return;
      setValue('portalData', templatePortalDefault);
    }, [templatePortalDefault, setValue]);

    useEffect(() => {
      if (!useTemplateConfig || !isFormLoaded || !accountyType || viewType !== 'create' || templateDefaultsApplied) return;

      const specialDefaultMappings = {
        type: 'c_type_jobs',
        c_type_jobs: 'c_type_jobs'
      };
      const passthroughDefaultKeys = new Set([
        'name',
        'applicantName',
        'applicantEmail',
        'area',
        'client',
        'subarea',
        'subclient',
        'c_modality',
        'count_post',
        'c_hierarchy',
        'c_area',
        'search_reason',
        'apto_discapacitados'
      ]);
      const normalizeDefaultValue = (rawValue) => {
        if (rawValue === null || rawValue === undefined || rawValue === '') return rawValue;

        if (typeof rawValue === 'string' && !/^-?\d+$/.test(rawValue.trim())) {
          return rawValue;
        }

        const parsedValue = Number.parseInt(rawValue, 10);
        return Number.isNaN(parsedValue) ? rawValue : parsedValue;
      };

      templateFields.forEach((field) => {
        if (!field || field.default === null || field.default === undefined) return;

        if (field.key === 'business_group_type') {
          setValue('portalData', field.default);
          return;
        }

        const targetField = (() => {
          if (accountyType === 'consultant') {
            if (field.key === 'area') return 'client';
            if (field.key === 'subarea') return 'subclient';
          }

          return specialDefaultMappings[field.key] || (passthroughDefaultKeys.has(field.key) ? field.key : null);
        })();
        if (!targetField) return;

        const normalizedDefaultValue = normalizeDefaultValue(field.default);
        setValue(targetField, normalizedDefaultValue);
        if (targetField === 'area') {
          setAreaSubareas(normalizedDefaultValue);
        }
        if (targetField === 'client') {
          setClientSubclients(normalizedDefaultValue);
        }
        if (targetField === 'c_modality' && !Number.isNaN(parseInt(normalizedDefaultValue, 10))) {
          setValueModality(parseInt(normalizedDefaultValue, 10));
        }
      });

      setTemplateDefaultsApplied(true);
    }, [
      isFormLoaded,
      accountyType,
      setAreaSubareas,
      setClientSubclients,
      setValue,
      setValueModality,
      templateDefaultsApplied,
      templateFields,
      useTemplateConfig,
      viewType
    ]);

    const onShowToast = (toastConfig = {}) => {
      const isError = toastConfig?.type === 'error' || toastConfig?.type === 'warning';
      showToast({
        message: toastConfig?.message || '',
        title: toastConfig?.title || '',
        iconClassName: isError ? 'hr-Alert' : 'hr-Checkmark',
        color: isError ? HrRed : HrPrimary
      });
    };

    const successSend = () => {
      setEditors({ descript: '', requirements: '', benefits: '' });
      setIsLoading(false);
      setModalSuccess(true);
      resetLocalityModal();
      resetSubarea();
      resetClientSubclients();
      resetAreaSubareas();
      setValueModality(1);

      reset({
        name: '',
        applicantName: '',
        applicantEmail: '',
        portalData: '',
        localityData: [],
        count_post: 1,
        attachedFiles: []
      });
    };

    const editorFields = [
      { key: 'requirements', title: t('requestVacancies.jobRequirements') },
      { key: 'benefits', title: t('requestVacancies.jobBenefits') },
      { key: 'descript', title: t('requestVacancies.jobDescription') }
    ];

    const filterDataRequest = (data) => {
      if (typeof data !== 'object' || data === null) return false;
      const dataFilter = Object.keys(data).reduce((acc, key) => {
        if (data[key] !== '' && data[key] !== null) acc[key] = data[key];
        return acc;
      }, {});

      ['area', 'subarea', 'client', 'subclient'].forEach((organizationFieldKey) => {
        if (dataFilter[organizationFieldKey] === false || dataFilter[organizationFieldKey] === 'false') {
          delete dataFilter[organizationFieldKey];
        }
      });

      if (useTemplateConfig) {
        const getPayloadKeysForTemplateField = (templateKey) => {
          if (accountyType !== 'consultant') return [templateKey];
          if (templateKey === 'area') return ['client'];
          if (templateKey === 'subarea') return ['subclient'];
          return [templateKey];
        };

        const removeFieldIfHidden = (templateKey, payloadKeys = [templateKey]) => {
          if (isFieldVisible(templateKey, false)) return;
          payloadKeys.forEach((payloadKey) => {
            delete dataFilter[payloadKey];
          });
        };

        removeFieldIfHidden('business_group_type', ['business_group_type', 'business_group_id', 'business_group_key', 'portalData']);
        removeFieldIfHidden('applicantName');
        removeFieldIfHidden('applicantEmail');
        removeFieldIfHidden('area', getPayloadKeysForTemplateField('area'));
        removeFieldIfHidden('subarea', getPayloadKeysForTemplateField('subarea'));
        removeFieldIfHidden('areaApprovalRequired');
        removeFieldIfHidden('subareaApprovalRequired');
        removeFieldIfHidden('name');
        removeFieldIfHidden('c_area');
        removeFieldIfHidden('c_subarea');
        removeFieldIfHidden('c_modality');
        removeFieldIfHidden('search_reason');
        removeFieldIfHidden('count_post');
        removeFieldIfHidden(['type', 'c_type_jobs'], ['c_type_jobs']);
        removeFieldIfHidden('apto_discapacitados');
        removeFieldIfHidden('experience');
        removeFieldIfHidden('c_hierarchy');
        removeFieldIfHidden(['languages', 'selectedLanguages'], ['selectedLanguages', 'languages']);
        removeFieldIfHidden('hasPeopleInCharge');
        removeFieldIfHidden('travel_availability');
        removeFieldIfHidden('requirements');
        removeFieldIfHidden('benefits');
        removeFieldIfHidden('attachedFile', ['attachedFiles']);
      }

      if (!Object.prototype.hasOwnProperty.call(dataFilter, 'selectedLanguages') && dataFilter.languages) {
        dataFilter.selectedLanguages = dataFilter.languages;
      }
      const localityData = dataFilter?.localityData ?? {};
      const localityDataFilter = Object.keys(localityData).reduce((acc, key) => {
        if (localityData[key] !== '' && localityData[key] !== null) acc[key] = localityData[key];
        return acc;
      }, {});

      if (useTemplateConfig) {
        if (!showCountryField) delete localityDataFilter.country;
        if (!showProvinceField) delete localityDataFilter.zona;
        if (!showLocalityField) delete localityDataFilter.locality;
      }

      if (Object.keys(localityDataFilter).length > 0) {
        dataFilter.localityData = localityDataFilter;
      } else {
        delete dataFilter.localityData;
      }

      return dataFilter;
    };


    {/* COMPANY: Área/Subárea */}
    {accountyType === 'company' && Array.isArray(dataViewRequest.areas) && dataViewRequest.areas.length > 0 && (
      <>
        <SelectForm
          title='Área'
          id='area'
          data={dataViewRequest.areas}
          control={control}
          formState={formState}
          isSearchable
          validate={{ required: false }}
          defaultValue={null}
          disabled={viewType === 'readOnly'}
          onChangee={({ value }) => {
            setAreaSubareas(value);
            setValue('subarea', '');
            setPrefilledCompany(true);
          }}
          errorPosition='bottom'
        />

        <SelectForm
          title='Subárea'
          id='subarea'
          data={dataViewRequest.subareas}
          control={control}
          formState={formState}
          isSearchable
          validate={{ required: false }}
          defaultValue={null}
          disabled={viewType === 'readOnly' || (dataViewRequest.subareas?.length ?? 0) === 0}
          errorPosition='bottom'
        />
      </>
    )}

    {/* CONSULTANT: Cliente/Subcliente */}
    {accountyType === 'consultant' &&
      Array.isArray(dataViewRequest.clients) &&
      dataViewRequest.clients.length > 0 && (
        <>
          <SelectForm
            title='Cliente'
            id='client'
            data={dataViewRequest.clients}
            control={control}
            formState={formState}
            isSearchable
            validate={{ required: false }}
            defaultValue={false}
            disabled={viewType === 'readOnly'}
            onChangee={({ value }) => {
              setClientSubclients(value);
              setValue('subclient', '');
              setPrefilledConsultant(true);
            }}
            errorPosition='bottom'
          />

          <SelectForm
            title='Subcliente'
            id='subclient'
            data={dataViewRequest.subclients}
            control={control}
            formState={formState}
            isSearchable
            validate={{ required: false }}
            defaultValue={null}
            disabled={viewType === 'readOnly' || (dataViewRequest.subclients?.length ?? 0) === 0}
            errorPosition='bottom'
          />
        </>
      )}

    const getBusinessGroupData = (selectedPortalValue, portalDataOptions) => {
      if (!selectedPortalValue) return {};
      const options = Array.isArray(portalDataOptions) ? portalDataOptions : [];
      const selectedOption = options.find((option) => `${option?.value}` === `${selectedPortalValue}`);
      if (!selectedOption) {
        if (isMongoObjectId(selectedPortalValue)) {
          return { business_group_type: 'microsite', business_group_id: selectedPortalValue, business_group_key: null };
        }
        if (selectedPortalValue === 'referrers_program' || selectedPortalValue === 'referents_program') {
          return { business_group_type: 'referrers_program' };
        }
        return { business_group_type: 'portal', business_group_key: selectedPortalValue };
      }

      const optionType = `${selectedOption?.type || ''}`.toLowerCase();
      const optionValue = `${selectedOption?.value || ''}`;
      const optionKey = selectedOption?.business_group_key ?? selectedOption?.key ?? null;
      const optionId = selectedOption?.business_group_id ?? null;

      if (
        optionType === 'referrers_program' ||
        optionValue === 'referrers_program' ||
        optionValue === 'referents_program'
      ) {
        return {
          business_group_type: 'referrers_program'
        };
      }

      if (optionType === 'microsite') {
        const micrositeId = optionId || (isMongoObjectId(optionValue) ? optionValue : null);
        const micrositeKey = optionKey || (!isMongoObjectId(optionValue) ? optionValue : null);

        return {
          business_group_type: 'microsite',
          business_group_id: micrositeId,
          business_group_key: micrositeKey
        };
      }

      return {
        business_group_type: 'portal',
        business_group_key: optionKey || optionValue
      };
    };

    const buildTemplateSubmissionPayload = (data) => {
      if (!useTemplateConfig || !templateConfig?.hash) return data;

      const payload = {};
      const isTemplateFieldEnabled = (fieldKey) => {
        if (fieldKey === 'localityData') {
          return showCountryField || showProvinceField || showLocalityField;
        }

        if (fieldKey === 'type') {
          return isFieldVisible(['type', 'c_type_jobs'], false);
        }

        if (fieldKey === 'attachedFile') {
          return isFieldVisible('attachedFile', false);
        }

        if (fieldKey === 'selectedLanguages') {
          return isFieldVisible(['languages', 'selectedLanguages'], false);
        }

        return isFieldVisible(fieldKey, false);
      };

      const getTemplatePayloadFieldSourceKey = (fieldKey) => {
        if (accountyType !== 'consultant') return fieldKey;
        if (fieldKey === 'area') return 'client';
        if (fieldKey === 'subarea') return 'subclient';
        return fieldKey;
      };

      const copyIfPresent = (sourceKey, targetKey = sourceKey) => {
        if (data[sourceKey] !== undefined) {
          payload[targetKey] = data[sourceKey];
        }
      };

      copyIfPresent('account');
      copyIfPresent('templateId');
      copyIfPresent('templateHash');
      copyIfPresent('recaptcha');

      templateFields.forEach((field) => {
        if (!field?.key) return;
        if (!isTemplateFieldEnabled(field.key)) return;

        if (field.key === 'localityData') {
          const localityPayload = {};
          if (showCountryField && data.localityData?.country !== undefined) {
            localityPayload.country = data.localityData.country;
          }
          if (showProvinceField && data.localityData?.zona !== undefined) {
            localityPayload.zona = data.localityData.zona;
          }
          if (showLocalityField && data.localityData?.locality !== undefined) {
            localityPayload.locality = data.localityData.locality;
          }
          if (Object.keys(localityPayload).length > 0) {
            payload.localityData = localityPayload;
          }
          return;
        }

        if (field.key === 'attachedFile') {
          copyIfPresent('attachedFiles');
          return;
        }

        if (field.key === 'business_group_type') {
          copyIfPresent('business_group_type');
          copyIfPresent('business_group_id');
          copyIfPresent('business_group_key');
          return;
        }

        const sourceKey = getTemplatePayloadFieldSourceKey(field.key);
        copyIfPresent(sourceKey, field.key);
        if (accountyType === 'consultant' && field.key === 'area') copyIfPresent('client', 'client');
        if (accountyType === 'consultant' && field.key === 'subarea') copyIfPresent('subclient', 'subclient');
      });

      copyIfPresent('descript');

      const customFieldAnswers = buildCustomFieldAnswersPayload(templateCustomFields, data.customFieldsAnswers);
      if (customFieldAnswers) payload.customFieldsAnswers = customFieldAnswers;

      if (payload.templateId !== undefined) {
        payload.templateId = normalizeMongoLikeIdValue(payload.templateId);
      }
      if (payload.business_group_id !== undefined) {
        payload.business_group_id = normalizeMongoLikeIdValue(payload.business_group_id);
      }

      return payload;
    };

    const handleSubmissionValidationErrors = (responseData) => {
      const backendMessages = Array.isArray(responseData?.message) ? responseData.message : [];
      let hasFieldError = false;

      backendMessages.forEach((backendError) => {
        const backendParam = `${backendError?.param || ''}`.trim();
        const backendMessage = `${backendError?.msg || ''}`.toLowerCase();

        if (
          backendParam === 'attachedFile' ||
          backendParam === 'attachedFiles' ||
          backendMessage.includes('attachedfile')
        ) {
          setError('attachedFiles', {
            type: 'server',
            message: 'Adjuntá al menos un archivo para continuar.'
          });
          hasFieldError = true;
        }
      });

      return hasFieldError;
    };

    const onSubmitUpdate = (data) => {
      setIsLoading(true);
      clearErrors('attachedFiles');

      if (isFieldRequired('attachedFile', false)) {
        const currentFiles = getValues('attachedFiles');
        if (!Array.isArray(currentFiles) || currentFiles.length === 0) {
          setError('attachedFiles', { type: 'manual', message: 'Adjuntá al menos un archivo para continuar.' });
          onShowToast({ visible: true, type: 'error', message: 'Revisá los campos marcados en rojo.' });
          setIsLoading(false);
          return;
        }
      }

      data.account = aliasAccount;
      Object.assign(data, getBusinessGroupData(data.portalData, portalDataOptions));
      // Normalize invalid hierarchy value
      if (data.c_hierarchy === '-') {
        data.c_hierarchy = null;
      }
      const dataFilter = filterDataRequest(data);
      if (dataFilter?.templateId !== undefined) {
        dataFilter.templateId = normalizeMongoLikeIdValue(dataFilter.templateId);
      }
      if (dataFilter?.business_group_id !== undefined) {
        dataFilter.business_group_id = normalizeMongoLikeIdValue(dataFilter.business_group_id);
      }
      if (Object.keys(errors).length > 0 || !dataFilter) {
        console.error('Error trying to create a vacancy request. Object data to store is empty');
        onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
        setIsLoading(false);
        return;
      }
      const requestPayload = buildTemplateSubmissionPayload(dataFilter);
      requestPayload.idRequisition = idRequisition || dataForm?._id;
      requestPayload.idRequisition = normalizeMongoLikeIdValue(requestPayload.idRequisition);
      const promise = updateRequest(requestPayload);
      promise
        .then((response) => {
          if (response.data.result === 'success') {
            templateChangeGuard.markRequestSubmitted();
            successSend();
          }
          if (response.data.result === 'error') {
            const hasFieldError = handleSubmissionValidationErrors(response.data);
            onShowToast({
              visible: true,
              type: 'error',
              message: hasFieldError ? 'Revisá los campos marcados en rojo.' : t(`general.error.support`)
            });
            setIsLoading(false);
          }
        })
        .catch((error) => {
          console.error('vacancy_request_update_request', error);
          onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
          setIsLoading(false);
        })
        .finally(() => setIsLoading(false));
    };

    const onSubmit = (data) => {
      setIsLoading(true);
      setIsCreated(true);
      clearErrors('attachedFiles');
      data.account = aliasAccount;
      Object.assign(data, getBusinessGroupData(data.portalData, portalDataOptions));
      if (templateConfig?._id || templateConfig?.id) {
        data.templateId = templateConfig?._id || templateConfig?.id;
      }
      if (viewType === 'create' && templateConfig?.hash) {
        data.templateHash = templateConfig.hash;
      }
      const dataFilter = filterDataRequest(data);
      if (dataFilter?.templateId !== undefined) {
        dataFilter.templateId = normalizeMongoLikeIdValue(dataFilter.templateId);
      }
      if (dataFilter?.business_group_id !== undefined) {
        dataFilter.business_group_id = normalizeMongoLikeIdValue(dataFilter.business_group_id);
      }
      if (Object.keys(errors).length > 0 || !dataFilter) {
        console.error('Error trying to create a vacancy request. Object data to store is empty');
        onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
        setIsLoading(false);
        return;
      }
      const requestPayload = buildTemplateSubmissionPayload(dataFilter);
      const promise =
        viewType === 'create' && templateConfig?.hash
          ? saveNewRequestByTemplate(requestPayload)
          : saveNewRequest(dataFilter);
      promise.then((response) => {
        if (response.data.result === 'success') {
          templateChangeGuard.markRequestSubmitted();
          successSend();
        }
        if (response.data.result === 'error') {
          const hasFieldError = handleSubmissionValidationErrors(response.data);
          if (!hasFieldError) {
            onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
          }
          setIsLoading(false);
        }
      });
      promise
        .catch((error) => {
          onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
          console.error('vacancy_request_create_request', error);
        })
        .finally(() => setIsLoading(false));
    };

    const onSubmitError = (formErrors) => {
      setIsLoading(false);
      if (isStepperMode && formErrors && Object.keys(formErrors).length > 0) {
        let firstErrorStep = steps.length - 1;
        for (const key of Object.keys(formErrors)) {
          const step = FIELD_TO_STEP[key];
          if (step !== undefined && step < firstErrorStep) {
            firstErrorStep = step;
          }
        }
        setActiveStep(firstErrorStep);
      }
    };

    const clickHandlerExecuteRecaptcha = useCallback(
      async (data) => {
        if (!executeRecaptcha) return;

        // Antes del recaptcha a propósito: si la plantilla cambió se frena el envío, y no tiene
        // sentido quemar un token de un solo uso en una solicitud que no va a salir.
        if (await templateChangeGuard.blockSubmitIfTemplateChanged()) return;

        const result = await executeRecaptcha(`sv_form_${viewType}`);
        data.recaptcha = result;
        if (viewType === 'create') onSubmit(data);
        else onSubmitUpdate(data);
      },
      [executeRecaptcha, onSubmit, onSubmitUpdate, viewType, templateChangeGuard]
    );

    const goToNextStep = () => {
      if (!isStepperMode) return;
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const goToPreviousStep = () => {
      if (!isStepperMode) return;
      if (activeStep === 0) {
        window.history.back();
        return;
      }
      setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const handleStepChange = (nextStep) => {
      if (!isStepperMode) return;
      setActiveStep(nextStep);
    };

    const approve = useMutation(approveRequest, {
      onSuccess: (data) =>
        handleMutationSuccess({
          data,
          queryClient,
          setShowModalApproveRequest,
          showToast: onShowToast,
          view: 'formRequest',
          t
        })
    });

    const modify = useMutation(modifyRequest, {
      onSuccess: (data) =>
        handleMutationModify({
          data,
          queryClient,
          setShowModalModifyRequest,
          showToast: onShowToast,
          view: 'formRequest',
          t
        })
    });

    const reject = useMutation(rejectRequest, {
      onSuccess: (data) =>
        handleMutationReject({
          data,
          queryClient,
          setShowModalRejectRequest,
          showToast: onShowToast,
          view: 'formRequest',
          t
        })
    });

    const delete1 = useMutation(deleteRequest, {
      onSuccess: (data) => {
        handleMutationDelete({
          data,
          queryClient,
          setShowModalDeleteRequest,
          showToast: onShowToast,
          view: 'formRequest',
          t
        });
        window.location.href = `/app/vacancy_request`;
      }
    });

    if (!isFormLoaded) {
      return (
        <HrFlexContainer justifyContent='center'>
          <Spinner className='hr-Charge' category='tertiaryGreen' />
        </HrFlexContainer>
      );
    }

    if (formLoadError) {
      return (
        <HrFlexContainer justifyContent='center' padding='32px'>
          <HrTitleH3 color={HrRed}>No se pudo cargar el formulario. Intentá nuevamente en unos minutos.</HrTitleH3>
        </HrFlexContainer>
      );
    }

    const sortList = (arr) => arr.sort((a, b) => a.label.localeCompare(b.label));
    const shouldShowGridSection = !isStepperMode || activeStep === 0 || activeStep === 1;
    const shouldShowCandidateSection = !isStepperMode || activeStep === 2;
    const publicModalRowId = dataForm?._id;
    const actionModalRowId = isPublicView ? publicModalRowId : actualRowId;
    const approveAction = isPublicView ? publicMutations?.approve : approve;
    const modifyAction = isPublicView ? publicMutations?.modify : modify;
    const rejectAction = isPublicView ? publicMutations?.reject : reject;

    return (
      <>
        <form
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '32px 16px'
          }}
        >
          <MainContentContainer>
            <FlexContainer justifyContent='flex-start' alignItems='center' gap='0px'>
              {showReadOnlyBackButton ? (
                <HrButtons
                  size='small'
                  category='cancel'
                  backgroundColor='#f4f4f4'
                  padding='0'
                  gap='8px'
                  onClick={() => navigate(`/app/vacancy_request`)}
                  style={{ backgroundColor: '#f4f4f4' }}
                >
                  <HrIconButton
                    style={{ fontSize: '24px', display: 'flex', alignItems: 'center' }}
                    className='hr-Carret-left'
                    color='#3b3f51'
                  />
                  <HrTitleH3 style={{ cursor: 'pointer' }}>{t('requestVacancies.vacancyApplication')}</HrTitleH3>
                </HrButtons>
              ) : (
                <HrTitleH3 style={{ cursor: 'pointer' }}>{t('requestVacancies.vacancyApplication')}</HrTitleH3>
              )}
            </FlexContainer>

            {isStepperMode && (
              <StepperContainer borderRadius='6px 6px 0 0' overflow='hidden'>
                <RequestFormStepper steps={steps} activeStep={activeStep} onStepChange={handleStepChange} />
              </StepperContainer>
            )}

            <GridContainerResponsive
              padding='32px'
              backgroundColor='#fff'
              margin={isStepperMode ? '0' : '16px 0 0 0'}
              borderRadius={isStepperMode ? '0' : '6px 6px 0 0'}
              ref={refCurrent}
              style={{ display: shouldShowGridSection ? undefined : 'none' }}
            >
              <div style={{ display: !isStepperMode || activeStep === 0 ? 'contents' : 'none' }}>
                {isFieldVisible('applicantName') && (
                  <Input
                    title={t('requestVacancies.applicantName')}
                    placeholder={t('requestVacancies.name')}
                    name='applicantName'
                    type='text'
                    register={register}
                    formState={formState}
                    validate={{
                      required: isFieldRequired('applicantName', true),
                      minLength: isFieldRequired('applicantName', true) ? 5 : 0,
                      maxLength: 100,
                      whiteSpace: true,
                      caracterSpecial: true
                    }}
                    disabled={viewType === 'readOnly'}
                    errorPosition='bottom'
                  />
                )}

                {isFieldVisible('applicantEmail') && (
                  <Input
                    title={t('requestVacancies.applicantEmail')}
                    placeholder={t('requestVacancies.applicantEmail')}
                    name='applicantEmail'
                    type='text'
                    register={register}
                    formState={formState}
                    validate={{
                      required: isFieldRequired('applicantEmail', true),
                      minLength: isFieldRequired('applicantEmail', true) ? 5 : 0,
                      maxLength: 100,
                      whiteSpace: true,
                      caracterSpecial: true,
                      mail: isFieldRequired('applicantEmail', true)
                    }}
                    disabled={viewType === 'readOnly'}
                    errorPosition='bottom'
                  />
                )}

                {shouldShowBusinessGroupField && (
                  <SelectForm
                    title='Unidad de negocio / Micrositio'
                    id='portalData'
                    data={portalDataOptions}
                    control={control}
                    formState={formState}
                    isSearchable
                    validate={{
                      required: isFieldRequired('business_group_type', false)
                    }}
                    defaultValue={shouldUsePortalDisplayFallback ? false : templatePortalDefault || false}
                    placeholder={portalSelectPlaceholder}
                    disabled
                    errorPosition='bottom'
                  />
                )}

                {/* COMPANY: Área/Subárea */}
                {showAreaField &&
                  accountyType === 'company' &&
                  ((Array.isArray(companyAreaOptions) && companyAreaOptions.length > 0) ||
                    hasSelectedArea ||
                    (viewType === 'readOnly' && dataForm?.area)) && (
                    <>
                      {showAreaField && (
                        <>
                          <FormFieldColumn flexDirection='column'>
                            <SelectForm
                              title='Área'
                              id='area'
                              data={companyAreaOptions}
                              control={control}
                              formState={formState}
                              isSearchable
                              validate={{ required: isAreaFieldRequired }}
                              defaultValue={shouldUseAreaDisplayFallback ? false : resolvedAreaValue || false}
                              placeholder={areaSelectPlaceholder}
                              disabled
                              errorPosition='bottom'
                            />
                          </FormFieldColumn>

                          {showAreaApprovalCheckbox && (
                            <ApprovalCheckboxContainer>
                              <Checkbox
                                label='Necesita aprobación del área'
                                name='areaApprovalRequired'
                                control={control}
                                defaultChecked={forceAreaApprovalRequired}
                                margin='0'
                                width='auto'
                                disabled
                              />

                              <span
                                className='hr-Info'
                                style={{ cursor: 'help', color: HrDarkGrey }}
                                data-tip='Se enviará automáticamente un mail al líder del área solicitando su aprobación.'
                                data-for='tooltip-area-approval'
                                aria-label='Información: aprobación del área'
                              />
                              <HrInfoTooltip id='tooltip-area-approval' place='top' effect='solid' delayShow={200} />
                            </ApprovalCheckboxContainer>
                          )}
                        </>
                      )}

                      {showSubareaField && (hasSelectedSubarea || (viewType === 'readOnly' && dataForm?.subarea)) && (
                        <>
                          <FormFieldColumn flexDirection='column'>
                            <SelectForm
                              title='Subárea'
                              id='subarea'
                              data={companySubareaOptions}
                              control={control}
                              formState={formState}
                              isSearchable
                              validate={{ required: shouldRequireSubarea }}
                              defaultValue={shouldUseSubareaDisplayFallback ? false : resolvedSubareaValue || false}
                              placeholder={subareaSelectPlaceholder}
                              disabled
                              errorPosition='bottom'
                            />
                          </FormFieldColumn>

                          {showSubareaApprovalCheckbox && (
                            <ApprovalCheckboxContainer>
                              <Checkbox
                                label='Necesita aprobación de la subarea'
                                name='subareaApprovalRequired'
                                control={control}
                                defaultChecked={effectiveForceSubareaApprovalRequired}
                                margin='0'
                                width='auto'
                                disabled
                              />

                              <span
                                className='hr-Info'
                                style={{ cursor: 'help', color: HrDarkGrey }}
                                data-tip='Se enviará automáticamente un mail al líder de la subárea solicitando su aprobación.'
                                data-for='tooltip-subarea-approval'
                                aria-label='Información: aprobación de la subárea'
                              />
                              <HrInfoTooltip id='tooltip-subarea-approval' place='top' effect='solid' delayShow={200} />
                            </ApprovalCheckboxContainer>
                          )}
                        </>
                      )}
                    </>
                  )}

                {/* CONSULTANT: Cliente/Subcliente */}
                {isFieldVisible(['client', 'subclient']) &&
                  accountyType === 'consultant' &&
                  ((Array.isArray(consultantClientOptions) && consultantClientOptions.length > 0) ||
                    hasSelectedClient ||
                    (viewType === 'readOnly' && dataForm?.client)) && (
                    <>
                      {isFieldVisible('client') && (
                        <SelectForm
                          title='Cliente'
                          id='client'
                          data={consultantClientOptions}
                          control={control}
                          formState={formState}
                          isSearchable
                          validate={{ required: isFieldRequired('client', false) }}
                          defaultValue={resolvedClientValue || false}
                          disabled
                          errorPosition='bottom'
                        />
                      )}

                      {isFieldVisible('subclient') &&
                        (hasSelectedSubclient || (viewType === 'readOnly' && dataForm?.subclient)) && (
                          <SelectForm
                            title='Subcliente'
                            id='subclient'
                            data={consultantSubclientOptions}
                            control={control}
                            formState={formState}
                            isSearchable
                            validate={{ required: isFieldRequired('subclient', false) }}
                            defaultValue={resolvedSubclientValue || false}
                            disabled
                            errorPosition='bottom'
                          />
                        )}
                    </>
                  )}

                {showLocalityFields && (
                  <>
                    {showCountryField && (
                      <SelectForm
                        title={t('requestVacancies.country')}
                        id='localityData.country'
                        data={dataViewRequest.countries}
                        control={control}
                        formState={formState}
                        isSearchable
                        validate={{ required: isLocalityFieldRequired('country', true) }}
                        disabled={viewType === 'readOnly'}
                        onChangee={(data) => {
                          filterProvinces(data.value);
                          setValue('localityData.locality', null);
                          setValue('localityData.zona', null);
                        }}
                        errorPosition='bottom'
                      />
                    )}

                    {showProvinceField && (
                      <SelectForm
                        title={t('requestVacancies.provinces')}
                        id='localityData.zona'
                        data={dataViewRequest.provinces}
                        control={control}
                        formState={formState}
                        isSearchable
                        validate={{
                          required:
                            isLocalityFieldRequired('zona', true) &&
                            (valueModality === 1 || valueModality === 3) &&
                            (!showCountryField ||
                              getValues('localityData.country') ||
                              getValues('localityData.country') !== null) &&
                            dataViewRequest.provinces.length > 0
                        }}
                        disabled={dataViewRequest.provinces.length === 0 || viewType === 'readOnly'}
                        onChangee={({ value }) => {
                          filterLocalities(value);
                          setValue('localityData.locality', null);
                        }}
                        errorPosition='bottom'
                      />
                    )}

                    {showLocalityField && (
                      <SelectForm
                        title={t('requestVacancies.localities')}
                        id='localityData.locality'
                        data={dataViewRequest.localities}
                        control={control}
                        formState={formState}
                        isSearchable
                        validate={{
                          required:
                            isLocalityFieldRequired('locality', true) &&
                            (valueModality === 1 || valueModality === 3) &&
                            (!showProvinceField ||
                              getValues('localityData.zona') ||
                              getValues('localityData.zona') !== null) &&
                            dataViewRequest.localities.length > 0 &&
                            dataViewRequest.provinces.length > 0
                        }}
                        disabled={
                          dataViewRequest.localities.length === 0 ||
                          viewType === 'readOnly' ||
                          dataViewRequest.provinces.length === 0
                        }
                        errorPosition='bottom'
                      />
                    )}
                  </>
                )}

                <CustomFieldsAnswers
                  fields={templateCustomFields}
                  tab={CUSTOM_FIELD_TABS.GENERAL}
                  register={register}
                  control={control}
                  formState={formState}
                  watch={watch}
                  disabled={viewType === 'readOnly'}
                />
              </div>

              <div style={{ display: !isStepperMode || activeStep === 1 ? 'contents' : 'none' }}>
                {isFieldVisible('name') && (
                  <Input
                    title={t('requestVacancies.jobTitle')}
                    placeholder={t('requestVacancies.jobTitle')}
                    name='name'
                    type='text'
                    register={register}
                    formState={formState}
                    validate={{
                      required: isFieldRequired('name', true),
                      minLength: isFieldRequired('name', true) ? 5 : 0,
                      maxLength: 100,
                      whiteSpace: true,
                      caracterSpecial: true
                    }}
                    disabled={viewType === 'readOnly'}
                    errorPosition='bottom'
                  />
                )}

                {isFieldVisible('c_area') && (
                  <SelectForm
                    title={t('requestVacancies.area')}
                    id='c_area'
                    data={sortList(dataViewRequest.c_areas)}
                    control={control}
                    formState={formState}
                    isSearchable
                    validate={{ required: isFieldRequired('c_area', true) }}
                    disabled={dataViewRequest.c_areas.length === 0 || viewType === 'readOnly'}
                    onChangee={({ value }) => {
                      setSubArea(value);
                      setValue('c_subarea', '');
                    }}
                    errorPosition='bottom'
                  />
                )}

                {isFieldVisible('c_subarea') && (
                  <SelectForm
                    title={t('requestVacancies.subarea')}
                    id='c_subarea'
                    data={sortList(dataViewRequest.c_subAreas)}
                    control={control}
                    formState={formState}
                    isSearchable
                    validate={{ required: isFieldRequired('c_subarea', false) }}
                    disabled={viewType === 'readOnly'}
                    errorPosition='bottom'
                  />
                )}

                {isFieldVisible('c_modality') && (
                  <SelectForm
                    title={t('requestVacancies.modalityJob')}
                    id='c_modality'
                    data={_modality}
                    control={control}
                    formState={formState}
                    isSearchable
                    validate={{ required: isFieldRequired('c_modality', true) }}
                    defaultValue={null}
                    disabled={viewType === 'readOnly'}
                    onChangee={({ value }) => {
                      setValueModality(value);
                    }}
                    errorPosition='bottom'
                  />
                )}

                {isFieldVisible('search_reason') && (
                  <SelectForm
                    title={t('requestVacancies.searchReason')}
                    id='search_reason'
                    data={sortList(searchReason)}
                    control={control}
                    formState={formState}
                    isSearchable
                    validate={{ required: isFieldRequired('search_reason', true) }}
                    disabled={viewType === 'readOnly'}
                    errorPosition='bottom'
                  />
                )}

                {isFieldVisible('count_post') && (
                  <Input
                    title={t('requestVacancies.positionsTocover')}
                    placeholder={t('requestVacancies.positionsTocoverPlaceholder')}
                    name='count_post'
                    type='number'
                    register={register}
                    formState={formState}
                    validate={{
                      required: isFieldRequired('count_post', true),
                      min: isFieldRequired('count_post', true) ? 1 : null,
                      max: isFieldRequired('count_post', true) ? 50 : null,
                      maxLength: isFieldRequired('count_post', true) ? 2 : 0,
                      whiteSpace: true,
                      caracterSpecial: false,
                      valueAsNumber: true
                    }}
                    disabled={viewType === 'readOnly'}
                  />
                )}

                {isFieldVisible(['type', 'c_type_jobs']) && (
                  <SelectForm
                    title={t('requestVacancies.jobType')}
                    id='c_type_jobs'
                    data={job}
                    control={control}
                    formState={formState}
                    isSearchable
                    validate={{ required: isFieldRequired('type', false) || isFieldRequired('c_type_jobs', false) }}
                    disabled={viewType === 'readOnly'}
                    errorPosition='bottom'
                  />
                )}

                {isFieldVisible('apto_discapacitados') && (
                  <Checkbox
                    label={t('requestVacancies.VacancySuitableForPeopleWithDisabilities')}
                    name='apto_discapacitados'
                    control={control}
                    defaultChecked={false}
                    margin='24px 0 0 0'
                  />
                )}

                <CustomFieldsAnswers
                  fields={templateCustomFields}
                  tab={CUSTOM_FIELD_TABS.VACANCY_DETAILS}
                  register={register}
                  control={control}
                  formState={formState}
                  watch={watch}
                  disabled={viewType === 'readOnly'}
                />
              </div>
            </GridContainerResponsive>

            {shouldShowCandidateSection && (
              <FlexContainer
                backgroundColor='#fff'
                padding={isStepperMode ? '32px 32px 16px 32px' : '0px 32px 16px 32px'}
                justifyContent='center'
                flexDirection='column'
                alignItems='stretch'
                gap='24px'
                borderRadius={isStepperMode ? '6px 6px 0 0' : '0'}
                columns='1'
              >
                <CandidateFieldsGrid>
                  {isFieldVisible('experience') && (
                    <SelectForm
                      title='¿Con experiencia?'
                      id='experience'
                      data={[
                        { value: true, label: 'Sí' },
                        { value: false, label: 'No' }
                      ]}
                      control={control}
                      formState={formState}
                      isSearchable={false}
                      validate={{ required: isFieldRequired('experience', false) }}
                      disabled={viewType === 'readOnly'}
                      errorPosition='bottom'
                    />
                  )}

                  {isFieldVisible('c_hierarchy') && (
                    <SelectForm
                      title={t('requestVacancies.hierarchy')}
                      id='c_hierarchy'
                      data={dataViewRequest.hierarchies}
                      control={control}
                      formState={formState}
                      isSearchable
                      validate={{ required: isFieldRequired('c_hierarchy', true) }}
                      disabled={viewType === 'readOnly'}
                      errorPosition='bottom'
                    />
                  )}

                  {isFieldVisible(['languages', 'selectedLanguages']) && (
                    <SelectForm
                      title='Idiomas'
                      id='selectedLanguages'
                      data={languageOptions}
                      control={control}
                      formState={formState}
                      isSearchable
                      validate={{
                        required: isFieldRequired('languages', false) || isFieldRequired('selectedLanguages', false)
                      }}
                      disabled={viewType === 'readOnly'}
                      errorPosition='bottom'
                    />
                  )}

                  {isFieldVisible('hasPeopleInCharge') && (
                    <SelectForm
                      title='¿Gente a cargo?'
                      id='hasPeopleInCharge'
                      data={[
                        { value: true, label: 'Sí' },
                        { value: false, label: 'No' }
                      ]}
                      control={control}
                      formState={formState}
                      isSearchable={false}
                      validate={{ required: isFieldRequired('hasPeopleInCharge', false) }}
                      disabled={viewType === 'readOnly'}
                      errorPosition='bottom'
                    />
                  )}

                  {isFieldVisible('travel_availability') && (
                    <SelectForm
                      title='Disponibilidad para viajar'
                      id='travel_availability'
                      data={[
                        { value: true, label: 'Sí' },
                        { value: false, label: 'No' }
                      ]}
                      control={control}
                      formState={formState}
                      isSearchable={false}
                      validate={{ required: isFieldRequired('travel_availability', false) }}
                      disabled={viewType === 'readOnly'}
                      errorPosition='bottom'
                    />
                  )}
                </CandidateFieldsGrid>

                {editorFields
                  .filter(({ key }) => {
                    if (key === 'descript') return true;
                    if (!useTemplateConfig) return true;
                    return isFieldVisible(key);
                  })
                  .map(({ key, title }) => (
                    <EditorConvertToHTML
                      key={key}
                      title={title}
                      name={key}
                      validate={{
                        required: key === 'descript' ? false : isFieldRequired(key, false),
                        minLength: (key === 'descript' ? false : isFieldRequired(key, false)) ? 10 : 0
                      }}
                      formState={formState}
                      control={control}
                      id={key}
                      width='100%'
                      normalConfig
                      defVal={editors[key]}
                      disabled={viewType === 'readOnly'}
                    />
                  ))}

                {viewType !== 'readOnly' && isFieldVisible('attachedFile') && (
                  <MultipleFile
                    limitFiles={3}
                    sizeFiles={15000000}
                    accept='.pdf, .doc, .docx, .csv, .xls'
                    control={control}
                    register={register}
                    setValue={setValue}
                    unregister={unregister}
                    getValues={getValues}
                    viewType={viewType}
                    trigger={trigger}
                    reset={reset}
                    setIsUploadingFile={setIsUploadingFile}
                    existingFiles={viewType === 'edit' ? dataForm?.attachedFiles : []}
                    onOpenExistingFile={toggleViewerDocs}
                    fieldError={errors.attachedFiles}
                    clearFieldError={() => clearErrors('attachedFiles')}
                    required={isFieldRequired('attachedFile', false)}
                  />
                )}

                {viewType === 'readOnly' && dataForm?.attachedFiles && (
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <HrParagraph fontWeight='600' fontSize='14px' margin='0 0 8px 0'>
                      Archivo adjunto
                    </HrParagraph>

                    <HrFlexContainer flexDirection='column' gap='12px' alignItems='flex-start'>
                      {dataForm.attachedFiles.map((_document) => (
                        <HrFlexContainer
                          key={_document.fileName}
                          padding='8px'
                          backgroundColor='#f4f4f4'
                          borderRadius='8px'
                          width='fit-content'
                        >
                          <span style={{ wordBreak: 'break-word' }}>{_document.fileName}</span>
                          <button
                            className='hr-Eye-open'
                            style={{
                              color: '#24bb72',
                              cursor: 'pointer',
                              border: 'none',
                              background: 'transparent'
                            }}
                            onClick={() => toggleViewerDocs(_document)}
                            type='button'
                            aria-label='Ver documento'
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') toggleViewerDocs(_document);
                            }}
                            tabIndex={0}
                          />
                        </HrFlexContainer>
                      ))}
                    </HrFlexContainer>
                  </div>
                )}

                {showViewerDocs && (
                  <Modal setShowModal={setShowViewerDocs}>
                    <div
                      style={{
                        maxWidth: '500px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        padding: '20px 16px 18px 16px'
                      }}
                    >
                      <TitleDocViewer style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {showDocuments.fileName}
                      </TitleDocViewer>
                    </div>
                    <IframeViewer
                      title='doc-viewer'
                      id='doc-viewer-iframe'
                      src={`https://docs.google.com/viewer?url=${showDocuments.uri}&chrome=false&embedded=true`}
                    />
                    <HrFlexContainer justifyContent='flex-end' style={{ gap: '8px', padding: '16px' }}>
                      <DownloadDocButton
                        as='a'
                        category='primary'
                        size='large'
                        gap='4px'
                        href={getSameOriginDownloadUrl(showDocuments.uri)}
                        download={showDocuments.fileName}
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label='Descargar documento'
                      >
                        <HrIconButton className='hr-Download' />
                        Descargar
                      </DownloadDocButton>
                      <HrButtons type='button' category='cancel' size='large' onClick={() => setShowViewerDocs(false)}>
                        Cerrar
                      </HrButtons>
                    </HrFlexContainer>
                  </Modal>
                )}
                {/* Los campos custom van al final de todo el contenido de la pestaña */}
                <CustomFieldsAnswers
                  fields={templateCustomFields}
                  tab={CUSTOM_FIELD_TABS.CANDIDATE_DETAILS}
                  register={register}
                  control={control}
                  formState={formState}
                  watch={watch}
                  disabled={viewType === 'readOnly'}
                />
              </FlexContainer>
            )}

            {/* Fuera de las secciones por pestaña: el aviso tiene que poder aparecer en cualquiera */}
            <TemplateChangedModal
              isOpen={templateChangeGuard.isDialogOpen}
              onClose={templateChangeGuard.closeDialog}
              onRefreshKeepingDraft={() => templateChangeGuard.refreshForm({ keepDraft: true, values: getValues() })}
              onRefreshDiscardingDraft={() => templateChangeGuard.refreshForm({ keepDraft: false })}
            />

            <FlexContainerButton>
              <FooterActionGroup justifyContent='flex-start'>
                {viewType === 'create' && isStepperMode && !isPublicView && !isTemplateFlow && (
                  <HrButtons type='button' category='cancel' size='large' onClick={goToPreviousStep}>
                    Cancelar
                  </HrButtons>
                )}
              </FooterActionGroup>

              <FooterActionGroup justifyContent='flex-end'>
                {isStepperMode && activeStep > 0 && (
                  <HrButtons type='button' category='tertiaryGreen' size='large' gap='4px' onClick={goToPreviousStep}>
                    <HrIconButton className='hr-Carret-left' color={HrPrimary} />
                    Anterior
                  </HrButtons>
                )}

                {isStepperMode && activeStep < steps.length - 1 && (
                  <HrButtons
                    type='button'
                    category='tertiaryGreen'
                    size='large'
                    disabled={viewType === 'create' && (isLoading || isUploadingFile)}
                    onClick={goToNextStep}
                    gap='4px'
                  >
                    Siguiente
                    <HrIconButton className='hr-Carret-right' color={HrPrimary} />
                  </HrButtons>
                )}

                {viewType === 'create' && isStepperMode && activeStep === steps.length - 1 && (
                  <HrButton
                    type='button'
                    category='primary'
                    size='large'
                    text='Enviar solicitud'
                    loading={isLoading}
                    iconColor='#fff'
                    disabled={isLoading || isUploadingFile}
                    onClick={handleSubmit(clickHandlerExecuteRecaptcha, onSubmitError)}
                  />
                )}

                {viewType === 'create' && !isStepperMode && (
                  <HrButtonRequest
                    type='button'
                    category='primary'
                    size='large'
                    text='Enviar solicitud'
                    loading={isLoading}
                    iconColor='#fff'
                    disabled={isLoading || isUploadingFile}
                    onClick={handleSubmit(clickHandlerExecuteRecaptcha, onSubmitError)}
                  />
                )}

                {viewType === 'edit' && (!isStepperMode || activeStep === steps.length - 1) && (
                  <HrButton
                    type='button'
                    category='primary'
                    size='large'
                    text='Guardar cambios'
                    loading={isLoading}
                    iconColor='#fff'
                    disabled={isLoading || isUploadingFile}
                    onClick={handleSubmit(clickHandlerExecuteRecaptcha, onSubmitError)}
                  />
                )}

                {(!isStepperMode || activeStep === steps.length - 1) &&
                  !isPublicView &&
                  !dataForm?.isDeleted &&
                  viewType === 'readOnly' && (
                    <HrButtons
                      title={t('requestVacancies.colaborativeSelection.modal.rejectRequest.deleteRequest')}
                      size='large'
                      category='secondary'
                      padding='16px'
                      gap='0px'
                      onClick={(e) => {
                        e.preventDefault();
                        setActualRowId(dataForm._id);
                        setShowModalDeleteRequest(true);
                      }}
                      disabled={isUploadingFile}
                    >
                      <i className='hr-Trash' style={{ fontSize: '16px', color: '#fff' }} />
                      {t('general.button.delete')}
                    </HrButtons>
                  )}

                {(!isStepperMode || activeStep === steps.length - 1) &&
                  !isPublicView &&
                  viewType === 'readOnly' &&
                  dataForm?.status === 0 && (
                    <>
                      <HrButtons
                        title={t('requestVacancies.colaborativeSelection.modal.rejectRequest.title')}
                        size='large'
                        category='tertiaryGray'
                        padding='16px'
                        gap='4px'
                        onClick={(e) => {
                          e.preventDefault();
                          setActualRowId(dataForm._id);
                          setShowModalRejectRequest(true);
                        }}
                        disabled={isUploadingFile}
                      >
                        <i className='hr-Close' style={{ fontSize: '16px' }} />
                        {t('general.button.reject')}
                      </HrButtons>
                      <HrButtons
                        title={t('requestVacancies.colaborativeSelection.modal.modifyRequest.title')}
                        size='large'
                        category='tertiaryGreen'
                        padding='16px'
                        gap='4px'
                        onClick={(e) => {
                          e.preventDefault();
                          setActualRowId(dataForm._id);
                          setShowModalModifyRequest(true);
                        }}
                        disabled={isUploadingFile}
                      >
                        <i className='hr-Pencil' style={{ fontSize: '16px', color: '#24bb72' }} />
                        {t('requestVacancies.colaborativeSelection.modal.modifyRequest.title')}
                      </HrButtons>
                      <HrButtons
                        title={t('requestVacancies.colaborativeSelection.modal.approveRequest.title')}
                        size='large'
                        category='primary'
                        padding='16px'
                        gap='4px'
                        onClick={(e) => {
                          e.preventDefault();
                          setActualRowId(dataForm._id);
                          setShowModalApproveRequest(true);
                        }}
                        disabled={isUploadingFile}
                      >
                        <i className='hr-Checkmark' style={{ fontSize: '16px', color: '#fff' }} />
                        {t('general.button.approve')}
                      </HrButtons>
                    </>
                  )}

                {(!isStepperMode || activeStep === steps.length - 1) &&
                  !isPublicView &&
                  viewType === 'readOnly' &&
                  dataForm?.status === 1 && (
                    <HrButtons
                      title={`Ir a la vacante ${dataForm.isDraft ? 'borrador' : ''}`}
                      size='large'
                      category='tertiaryGreen'
                      padding='16px'
                      gap='4px'
                      onClick={(e) => {
                        e.preventDefault();
                        if (dataForm.isDeleted) {
                          onShowToast({ visible: true, type: 'error', message: t(`crudVacancy.infoVacancyDeleted`) });
                          return;
                        }
                        window.open(
                          `/app/vacancy/${
                            dataForm.isDraft ? `edit/${dataForm.idVacancy}` : `getVacancy/${dataForm.idVacancy}/20`
                          }`,
                          `_blank`
                        );
                      }}
                    >
                      <i className='hr-File' style={{ fontSize: '16px', color: '#24bb72' }} />
                      Ir a la vacante
                      {dataForm.isDraft && ' borrador'}
                    </HrButtons>
                  )}

                {(!isStepperMode || activeStep === steps.length - 1) && canShowPublicDecisionActions && (
                  <>
                    <HrButtons
                      title={t('requestVacancies.colaborativeSelection.modal.rejectRequest.title')}
                      size='large'
                      category='tertiaryGray'
                      padding='16px'
                      gap='4px'
                      onClick={(e) => {
                        e.preventDefault();
                        setShowModalRejectRequest(true);
                      }}
                      disabled={false}
                    >
                      <i className='hr-Close' style={{ fontSize: '16px' }} />
                      {t('general.button.reject')}
                    </HrButtons>
                    <HrButtons
                      title={t('requestVacancies.colaborativeSelection.modal.modifyRequest.title')}
                      size='large'
                      category='tertiaryGreen'
                      padding='16px'
                      gap='4px'
                      onClick={(e) => {
                        e.preventDefault();
                        setShowModalModifyRequest(true);
                      }}
                      disabled={false}
                    >
                      <i className='hr-Pencil' style={{ fontSize: '16px', color: '#24bb72' }} />
                      {t('requestVacancies.colaborativeSelection.modal.modifyRequest.title')}
                    </HrButtons>
                    <HrButtons
                      title={t('requestVacancies.colaborativeSelection.modal.approveRequest.title')}
                      size='large'
                      category='primary'
                      padding='16px'
                      gap='4px'
                      onClick={(e) => {
                        e.preventDefault();
                        setShowModalApproveRequest(true);
                      }}
                      disabled={false}
                    >
                      <i className='hr-Checkmark' style={{ fontSize: '16px', color: '#fff' }} />
                      {t('general.button.approve')}
                    </HrButtons>
                  </>
                )}
              </FooterActionGroup>

              {modalSucces && (
                <ModalSuccessRequest
                  width='400px'
                  modalSucces={modalSucces}
                  setModalSuccess={setModalSuccess}
                  refCurrent={refCurrent}
                  isCreated={isCreated}
                />
              )}
            </FlexContainerButton>
          </MainContentContainer>
        </form>

        {showModalApproveRequest &&
          (isPublicView ? (
            <PublicApproveRequestModal
              actualRowId={publicModalRowId}
              approve={approveAction}
              setShowModalApproveRequest={setShowModalApproveRequest}
            />
          ) : (
            <ApproveRequestModal
              actualRowId={dataForm._id}
              approve={approveAction}
              setShowModalApproveRequest={setShowModalApproveRequest}
            />
          ))}
        {showModalModifyRequest && (
          <ModifyRequestModal
            actualRowId={actionModalRowId}
            modify={modifyAction}
            setShowModalModifyRequest={setShowModalModifyRequest}
            onShowToast={onShowToast}
          />
        )}
        {showModalRejectRequest && (
          <RejectRequestModal
            actualRowId={actionModalRowId}
            reject={rejectAction}
            setShowModalRejectRequest={setShowModalRejectRequest}
            onShowToast={onShowToast}
          />
        )}
        {showModalDeleteRequest && (
          <DeleteRequestModal
            actualRowId={actualRowId}
            delete1={delete1}
            setShowModalDeleteRequest={setShowModalDeleteRequest}
            onShowToast={onShowToast}
          />
        )}
      </>
    );
  }
);
